import React, { useRef, useState } from 'react';
import Ajv from 'ajv';
import {
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@kambi/spark';
import {
    getOutcomeLabel,
    getBetOfferLabel,
    getPotentialPayout,
    getOriginalPotentialPayout,
    getPotentialPayoutSystem,
    formatNumberWithCurrency,
    formatCurrencyWithUnicodeSymbol,
    formatDecimalOdds,
    getAmericanOdds,
    getFractionalOdds,
    formatOdds,
    getSystemCombinations,
} from '@kambi/betting-client-helpers';

import { UtilsTypes } from 'constants/utilsTypes';
import classes from './TestBlock.module.css';
import CodeBlock from '../code-block/CodeBlock';

const getTranslationCallback = (translationKey) => {
    if (translationKey === 'common.startingPrice') {
        return 'myTestStartingPrice';
    }
};

const TestBlock = ({ validationSchema, title, type }) => {
    const ajv = new Ajv({ allErrors: true });
    const validate = ajv.compile(validationSchema || {});

    const [output, setOutput] = useState('Output');
    const [validationErrors, setValidationErrors] = useState([]);
    const textareaEl = useRef();

    const processInput = (type) => {
        let parsedTextareaValue;

        try {
            parsedTextareaValue = JSON.parse(textareaEl.current.value);
            if (validationSchema && !validate(parsedTextareaValue)) {
                setValidationErrors(validate.errors);
                return;
            }
            setValidationErrors([]);
        } catch (err) {
            //eslint-disable-next-line
            alert(
                'Error in parsing the input value! Please enter them again and make sure you use the correct syntax: {"key": "value"}',
            );
            return;
        }

        let output;
        switch (type) {
            case UtilsTypes.OUTCOME_LABEL:
                output = getOutcomeLabel(parsedTextareaValue);
                break;
            case UtilsTypes.BETOFFER_LABEL:
                output = getBetOfferLabel(parsedTextareaValue);
                break;
            case UtilsTypes.POTENTIAL_PAYOUT:
                output = getPotentialPayout(parsedTextareaValue);
                break;
            case UtilsTypes.ORIGINAL_POTENTIAL_PAYOUT:
                output = getOriginalPotentialPayout(parsedTextareaValue);
                break;
            case UtilsTypes.POTENTIAL_PAYOUT_SYSTEM:
                output = getPotentialPayoutSystem(parsedTextareaValue);
                break;
            case UtilsTypes.FORMAT_NUMBER_WITH_CURRENCY:
                output = formatNumberWithCurrency(parsedTextareaValue);
                break;
            case UtilsTypes.FORMAT_CURRENCY_WITH_UNICODE_SYMBOL:
                output = formatCurrencyWithUnicodeSymbol(parsedTextareaValue);
                break;
            case UtilsTypes.FORMAT_DECIMAL_ODDS:
                output = formatDecimalOdds(parsedTextareaValue);
                break;
            case UtilsTypes.GET_AMERICAN_ODDS:
                output = getAmericanOdds(parsedTextareaValue);
                break;
            case UtilsTypes.FORMAT_ODDS:
                output = formatOdds({
                    ...parsedTextareaValue,
                    getTranslationCallback,
                });
                break;
            case UtilsTypes.GET_FRACTIONAL_ODDS:
                output = getFractionalOdds(parsedTextareaValue);
                break;
            case UtilsTypes.SYSTEM_COMBINATIONS:
                const allCombinations = JSON.stringify(
                    getSystemCombinations(parsedTextareaValue),
                    null,
                    2,
                );
                const INITIAL_SLICE_BREAKPOINT = 150;
                const initialDisplayedCode = allCombinations.slice(
                    0,
                    INITIAL_SLICE_BREAKPOINT,
                );
                const moreCode = allCombinations.slice(
                    INITIAL_SLICE_BREAKPOINT + 1,
                    allCombinations.length,
                );
                output = (
                    <CodeBlock
                        initialDisplayedCode={initialDisplayedCode}
                        moreCode={moreCode.length > 0 ? moreCode : undefined}
                        variant="result"
                    />
                );
                break;
            default:
                output = '';
        }
        setOutput(output);
    };

    const parseValidationErrors = (errors) => {
        let parsedErrors = [];
        for (let {
            dataPath,
            keyword,
            message,
            params: { additionalProperty, allowedValues },
        } of errors) {
            dataPath = dataPath.substring(1);
            switch (keyword) {
                case 'required':
                    parsedErrors.push(`${dataPath} ${message}`);
                    break;
                case 'additionalProperties':
                    parsedErrors.push(
                        `${dataPath} ${message}: ${additionalProperty}`,
                    );
                    break;
                case 'type':
                    parsedErrors.push(`${dataPath}'s ${keyword} ${message}`);
                    break;
                case 'enum':
                    const enumValues = allowedValues.reduce(
                        (acc, curr, index) =>
                            index === allowedValues.length - 1
                                ? `${acc}${curr}.`
                                : `${acc}${curr}, `,
                        '',
                    );
                    parsedErrors.push(
                        `${dataPath} ${message} of the ${keyword}: ${enumValues}`,
                    );
                    break;
                default:
                    parsedErrors.push(`${dataPath} ${keyword} - ${message}`);
            }
        }

        return parsedErrors;
    };

    const {
        TestBlock,
        Title,
        Content,
        TableTextarea,
        TableOutput,
        Textarea,
        Button: ButtonClass,
        Output,
        ValidationErrors,
        TableRowArea,
    } = classes;

    return (
        <div className={TestBlock}>
            <p className={Title}>{title}</p>
            <div className={Content}>
                <TableContainer component={Paper}>
                    <Table aria-label="basic table">
                        <TableHead>
                            <TableRow>
                                <TableCell>JSON Object</TableCell>
                                <TableCell align="left">Output</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow className={TableRowArea}>
                                <TableCell className={TableTextarea}>
                                    <div className={Textarea}>
                                        <textarea
                                            ref={textareaEl}
                                            id="KambiUtil-tryout-input"
                                            name="KambiUtil-tryout-input"
                                            placeholder={`${type} parameters`}
                                            rows="10"
                                        ></textarea>
                                    </div>
                                    <div className={ButtonClass}>
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            onClick={() => {
                                                processInput(type);
                                            }}
                                        >
                                            Run
                                        </Button>
                                    </div>
                                </TableCell>
                                <TableCell align="left" className={TableOutput}>
                                    {validationErrors.length ? (
                                        <ul className={ValidationErrors}>
                                            {parseValidationErrors(
                                                validationErrors,
                                            ).map((error) => (
                                                <li key={error}>{error}</li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <div className={Output}>{output}</div>
                                    )}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        </div>
    );
};

export default TestBlock;
